<template>
  <div class="container-fluid container-wrap col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 d-flex justify-content-between align-items-start">
          <div class="titleContainer">
            <h1 class="fw-bold page-title">
              {{ isEdit ? '이벤트 수정' : '이벤트 등록' }}
            </h1>
            <span class="pageDesc">{{ isEdit ? '이벤트 정보를 수정합니다.' : '새로운 이벤트를 등록합니다.' }}</span>
          </div>
          <!-- 컨트롤 패널 -->
          <div class="control-panel">
            <a-button
              v-if="showBackButton"
              @click="handleGoBack"
            >
              <template #icon><i class="fa fa-arrow-left"></i></template>
              이전 페이지로
            </a-button>
            <a-button
              @click="showResourceDrawer = true"
            >
              <template #icon><i class="fa fa-folder-open"></i></template>
              자료실
            </a-button>
            <a-button
              type="primary"
              :loading="submitting"
              class="yellow-button"
              @click="handleSave"
            >
              {{ isEdit ? '수정하기' : '등록하기' }}
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 이벤트 필터 섹션 (수정 모드에서만 표시) -->
    <div v-if="isEdit" class="event-filter-section">
      <a-row :gutter="[16, 12]" align="middle">
        <a-col :xs="24" :sm="24" :md="12" :lg="8" :xl="6">
          <label class="filter-label">
            이벤트 선택
            <span v-if="form.id" class="event-id-display">
              <a-typography-text
                :copyable="{ text: form.id }"
                style="color: #666; font-size: 12px;"
              >
                (ID: {{ form.id }})
              </a-typography-text>
            </span>
          </label>
          <a-select
            v-model:value="selectedEventId"
            style="width: 100%"
            placeholder="이벤트를 선택해주세요."
            :loading="eventsLoading"
            @change="handleEventFilterChange"
            showSearch
            :filter-option="filterEventOption"
          >
            <a-select-option v-for="event in eventList" :key="event._id" :value="event._id">
              {{ event.eventTitle }} ({{ getEventStatusText(event.eventStatus) }})
            </a-select-option>
          </a-select>
        </a-col>
        <a-col :xs="24" :sm="24" :md="12" :lg="16" :xl="18" class="filter-actions">
          <a-space>
            <a-button @click="goToParticipation">
              <template #icon><i class="fa fa-users"></i></template>
              참여 현황
            </a-button>
            <a-button @click="goToAnalytics">
              <template #icon><i class="fa fa-chart-bar"></i></template>
              분석
            </a-button>
            <a-button @click="goToReport">
              <template #icon><i class="fa fa-file-alt"></i></template>
              레포트
            </a-button>
          </a-space>
        </a-col>
      </a-row>
    </div>

    <div class="card-body px-0 pt-0 pb-2">
      <div class="card content-wrap-card col-sm-12 col-xs-12 form-card">
        <!-- Steps 추가 -->
        <a-steps
          v-model:current="currentStep"
          style="margin-bottom: 40px"
          @change="(step) => currentStep = step"
        >
          <a-step title="기본 정보" description="이벤트 기본 설정" />
          <a-step title="예산 관리" description="예산 회차별 관리" />
          <a-step title="페이지 디자인" description="이벤트 페이지 편집" />
          <a-step title="카카오 공유 설정" description="카카오 공유하기 버튼 설정" />
          <a-step title="배너/팝업 설정" description="쿠폰함 및 이탈방지 팝업 설정" />
          <a-step title="AARRR 메트릭" description="성과 측정 설정" />
        </a-steps>

        <a-form
          ref="formRef"
          :model="form"
          layout="vertical"
          :rules="rules"
          @finish="handleSubmit"
          @finishFailed="handleValidationFailed"
        >
          <!-- Step 1: 기본 정보 섹션 -->
          <div v-show="currentStep === 0">
            <basic-info-section
              :form="form"
              :is-edit="isEdit"
              @search-modal-open="handleSearchModalOpen"
              @hospital-search-modal-open="handleHospitalSearchModalOpen"
            />
          </div>

          <!-- Step 2: 예산 관리 -->
          <div v-show="currentStep === 1">
            <budget-section
              :form="form"
            />
          </div>

          <!-- Step 3: 페이지 디자인 -->
          <div v-show="currentStep === 2">
            <page-designer-section-v3
              :form="form"
              :is-edit="isEdit"
            />
          </div>

          <!-- Step 4: 카카오 공유 설정 -->
          <div v-show="currentStep === 3">
            <kakao-share-section
              :form="form"
              :is-edit="isEdit"
            />
          </div>

          <!-- Step 5: 배너/팝업 설정 -->
          <div v-show="currentStep === 4">
            <banner-popup-section
              :form="form"
              :is-edit="isEdit"
            />
          </div>

          <!-- Step 6: AARRR 메트릭 설정 -->
          <div v-show="currentStep === 5">
            <AARRRMetricsSection
              :form="form"
              :event-id="isEdit ? form.id : null"
              :api="apiInstance"
            />
          </div>

          <!-- 버튼 그룹 -->
          <div class="form-actions">
            <a-button v-if="currentStep > 0" @click="prevStep">이전</a-button>
            <a-button @click="handleCancel">취소</a-button>
            <a-button v-if="currentStep < 5" type="primary" @click="nextStep" class="yellow-button">
              다음
            </a-button>
            <a-button
              type="primary"
              :loading="submitting"
              class="yellow-button"
              @click="handleSave"
            >
              {{ isEdit ? '수정하기' : '등록하기' }}
            </a-button>
          </div>
        </a-form>
      </div>
    </div>

    <!-- 거래처 검색 모달 컴포넌트 추가 (주최자 검색용) -->
    <search-client-modal
      v-model:visible="showSearchModal"
      @select-client="handleSelectClient"
      @close-modal="showSearchModal = false"
    />

    <!-- 병원 검색 모달 컴포넌트 추가 (적용 대상 병원 검색용) -->
    <search-client-modal
      v-model:visible="showHospitalSearchModal"
      @select-client="handleSelectHospital"
      @close-modal="showHospitalSearchModal = false"
    />

    <!-- 자료실 Drawer -->
    <EventResourceDrawer
      v-model:visible="showResourceDrawer"
      :event-id="isEdit ? form.id : null"
      :api="apiInstance"
    />

    <!-- 진행중 이벤트 충돌 모달 -->
    <a-modal
      v-model:open="showOngoingConflictModal"
      title="진행중인 이벤트가 있습니다"
      :footer="null"
      @cancel="showOngoingConflictModal = false"
    >
      <div class="ongoing-conflict-modal">
        <div class="conflict-warning">
          <i class="fa fa-exclamation-triangle warning-icon"></i>
          <p class="warning-message">
            현재 진행중인 이벤트가 있어서 등록/수정이 불가합니다.
          </p>
        </div>
        <div v-if="conflictingEvent" class="conflict-details">
          <div class="detail-item">
            <label>진행중인 이벤트:</label>
            <span>{{ conflictingEvent.eventTitle }}</span>
          </div>
          <div class="detail-item">
            <label>주최자:</label>
            <span>
              {{ conflictingEvent.organizers?.map(org => org.organizerName).join(', ') }}
            </span>
          </div>
        </div>
        <div class="conflict-help">
          <p>
            동일한 주최자는 하나의 이벤트만 진행할 수 있습니다.<br />
            기존 이벤트를 '종료됨' 또는 '준비중' 상태로 변경 후 다시 시도해주세요.
          </p>
        </div>
        <div class="modal-footer">
          <a-button type="primary" class="yellow-button" @click="showOngoingConflictModal = false">
            확인
          </a-button>
        </div>
      </div>
    </a-modal>

    <!-- 진행중 변경 확인 모달 -->
    <a-modal
      v-model:open="showCertificateLinkModal"
      title="상태 변경 확인"
      :footer="null"
      @cancel="handleCertificateLinkCancel"
    >
      <div class="certificate-link-modal">
        <p class="modal-description">
          이벤트 상태를 <strong>'진행중'</strong>으로 변경합니다.
        </p>
        <div class="modal-footer">
          <a-button @click="handleCertificateLinkCancel">취소</a-button>
          <a-button
            type="primary"
            class="yellow-button"
            :loading="submitting"
            @click="handleCertificateLinkConfirm"
          >
            확인
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import eventsApis from './api';
import SearchClientModal from './components/SearchClientModal.vue';
import { twoBtnDialog } from "@/utils/utils";

// 컴포넌트 및 컴포저블 import
import BasicInfoSection from './components/sections/BasicInfoSection.vue';
import PageDesignerSectionV3 from './components/sections/PageDesignerSectionV3.vue';
import KakaoShareSection from './components/sections/KakaoShareSection.vue';
import BannerPopupSection from './components/sections/BannerPopupSection.vue';
import AARRRMetricsSection from './components/sections/AARRRMetricsSection.vue';
import EventResourceDrawer from './components/EventResourceDrawer.vue';
import BudgetSection from './components/sections/BudgetSection.vue';

import useEventForm from './composables/useEventForm';

export default {
  name: 'UpsertEvent',
  components: {
    SearchClientModal,
    BasicInfoSection,
    PageDesignerSectionV3,
    KakaoShareSection,
    BannerPopupSection,
    AARRRMetricsSection,
    EventResourceDrawer,
    BudgetSection,
  },
  setup(props) {
    // API 인스턴스
    const instance = getCurrentInstance();
    const apiInstance = ref(null);
    const route = useRoute();
    const router = useRouter();
    
    // API 인스턴스 초기화
    if (instance && instance.proxy) {
      apiInstance.value = eventsApis(instance.proxy.$apiInstance, instance.proxy.$store);
      console.log('API 인스턴스 초기화:', apiInstance.value);
    } else {
      console.error('Current instance not available, API initialization failed');
    }
    
    // useEventForm composable 사용
    const {
      formRef,
      form,
      rules,
      submitting,
      isEdit,
      activeTab,
      showSearchModal,
      
      handleSubmit: originalHandleSubmit,
      handleCancel,
      beforeUpload,
      handleSelectClient: originalHandleSelectClient,
      loadData
    } = useEventForm(apiInstance.value);
    
    // form.organizers가 정의되어 있지 않으면 초기화
    if (!form.organizers) {
      form.organizers = [];
    }
    
    // handleSelectClient 함수 백엔드 스키마에 맞게 오버라이드
    const handleSelectClient = (clients) => {
      console.log('거래처 선택됨:', clients);
      
      // clients 배열이 없거나 비어있으면 early return
      if (!clients || !Array.isArray(clients) || clients.length === 0) {
        console.error('선택된 거래처가 없습니다.');
        return;
      }
      
      // form.organizers 확인 및 초기화
      if (!form.organizers) {
        form.organizers = [];
      }
      
      // 선택된 거래처 정보로 form.organizers 업데이트
      form.organizers = [...clients];
      
      console.log('업데이트된 form.organizers:', form.organizers);
      
      // 필요한 경우 UI 업데이트 트리거 (reactivity 이슈 방지)
      if (instance && instance.proxy) {
        instance.proxy.$forceUpdate();
      }
    };
    
    // 폼 유효성 검사 실패 핸들러
    const handleValidationFailed = (errorInfo) => {
      console.error('폼 유효성 검사 실패:', errorInfo);
      
      if (instance && instance.proxy && instance.proxy.$message) {
        // 오류 필드 정보 가져오기
        const errorFields = errorInfo.errorFields || [];
        if (errorFields.length > 0) {
          // 첫 번째 오류 필드의 이름과 메시지 가져오기
          const firstErrorField = errorFields[0];
          const fieldName = firstErrorField.name[0] || '알 수 없는 필드';
          const errorMessage = firstErrorField.errors[0] || '입력값이 유효하지 않습니다';
          
          // 특정 필드에 대한 더 읽기 쉬운 이름 매핑
          const fieldNameMap = {
            organizers: '이벤트 진행주체',
            eventTitle: '이벤트 제목',
            scenarioType: '시나리오 유형',
            customScenario: '시나리오 설명',
            startDate: '시작일',
            endDate: '종료일',
            targetCustomers: '대상 고객',
            shareMessage: '공유 메시지',
            androidUrl: 'Android 앱 URL',
            iosUrl: 'iOS 앱 URL',
            surveyTitle: '설문 제목',
            reservationTitle: '예약 안내 제목',
            announcementDate: '당첨자 발표일',
            drawType: '당첨자 선정 방식'
          };
          
          // 토스트 메시지 표시
          instance.proxy.$message.error(`입력값 오류: ${fieldNameMap[fieldName] || fieldName} - ${errorMessage}`);
          
          // 해당 필드로 스크롤
          setTimeout(() => {
            const errorElement = document.querySelector(`.ant-form-item-has-error`);
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        } else {
          instance.proxy.$message.error('입력 내용을 확인해주세요.');
        }
      }
    };
    
    // 원래의 handleSubmit 함수를 래핑하여 필드명 변환 추가
    const handleSubmit = async (values) => {
      try {
        if (!apiInstance.value) {
          console.error('API 인스턴스가 초기화되지 않았습니다.');
          if (instance && instance.proxy && instance.proxy.$message) {
            instance.proxy.$message.error('등록에 필요한 서버 연결이 준비되지 않았습니다. 페이지를 새로고침 후 다시 시도해주세요.');
          } else {
            console.error('메시지 서비스를 사용할 수 없습니다.');
          }
          return;
        }
        
        // 폼 데이터에서 organizers 객체가 있으면 필드명 변환
        if (values.organizers && values.organizers.length > 0) {
          // 이미 변환되었는지 확인 (기존 데이터를 불러와서 수정하는 경우 대비)
          if (values.organizers[0].id !== undefined) {
            values.organizers = values.organizers.map(org => ({
              organizerId: String(org.id), // 문자열로 변환
              organizerName: org.name,
              type: org.type || 'HOSPITAL',
              representative: org.representative,
              organizerCno: org.businessNumber,
              directInput: org.isCustom
            }));
          }
          // 이미 변환된 경우 그대로 사용 (type 필드 확인)
          else {
            values.organizers = values.organizers.map(org => ({
              ...org,
              type: org.type || 'HOSPITAL' // type 필드가 없으면 기본값 추가
            }));
          }
        }
        
        console.log('API 인스턴스로 이벤트 등록 시작:', apiInstance.value);
        console.log('제출할 데이터:', values);

        // 원래의 handleSubmit 함수를 호출하되, apiInstance를 전달
        await originalHandleSubmit(values, apiInstance.value);
      } catch (error) {
        console.error('이벤트 등록/수정 중 오류가 발생했습니다:', error);
        if (instance && instance.proxy && instance.proxy.$message) {
          instance.proxy.$message.error('이벤트 등록/수정 중 오류가 발생했습니다.');
        } else {
          console.error('메시지 서비스를 사용할 수 없습니다.');
        }
        submitting.value = false;
      }
    };
    
    
    
    // 거래처 검색 모달 열기 (주최자 검색용)
    const handleSearchModalOpen = () => {
      showSearchModal.value = true;
    };

    // 병원 검색 모달 상태
    const showHospitalSearchModal = ref(false);

    // 병원 검색 모달 열기 (적용 대상 병원 검색용)
    const handleHospitalSearchModalOpen = () => {
      showHospitalSearchModal.value = true;
    };

    // 선택된 병원 처리 (적용 대상 병원 추가)
    const handleSelectHospital = (clients) => {
      console.log('병원 선택됨:', clients);

      if (!clients || !Array.isArray(clients) || clients.length === 0) {
        console.error('선택된 병원이 없습니다.');
        return;
      }

      // form.targetHospitals 초기화
      if (!form.targetHospitals) {
        form.targetHospitals = [];
      }

      // 선택된 병원을 targetHospitals에 추가
      clients.forEach(client => {
        // 중복 체크 (organizerId === hospitalId)
        const hospitalId = client.organizerId || client.id;
        const hospitalName = client.organizerName || client.name;

        const isDuplicate = form.targetHospitals.some(
          hospital => hospital.hospitalId === hospitalId
        );

        if (!isDuplicate) {
          form.targetHospitals.push({
            hospitalId: hospitalId,
            hospitalName: hospitalName
          });
          console.log('병원 추가됨:', { hospitalId, hospitalName });
        } else {
          console.log('이미 추가된 병원:', hospitalId);
          if (instance && instance.proxy && instance.proxy.$message) {
            instance.proxy.$message.warning(`${hospitalName}은(는) 이미 추가되어 있습니다.`);
          }
        }
      });

      console.log('업데이트된 targetHospitals:', form.targetHospitals);
    };
    
    // 활성 탭 업데이트
    const updateActiveTab = (newTab) => {
      console.log('updateActiveTab', newTab);
      if(
        newTab === 'friend-apply' 
        || newTab === 'friend-share' 
      ) {
        newTab = 'friend';
        return false;
      }
      if(
        newTab === 'other-people-apply' 
        || newTab === 'other-people-share'
      ) {
        newTab = 'other-people';
        return false;
      }
      if (activeTab.value !== newTab) {
        activeTab.value = newTab;
        console.log(`활성 탭이 ${newTab}으로 변경되었습니다.`);
      }
    };
    
    // 기능 활성화 상태 업데이트
    const updateFeatureEnabled = (feature, enabled) => {
      try {
        const prevValue = form[`${feature}Enabled`];
        form[`${feature}Enabled`] = enabled;
        console.log(`${feature} 기능이 ${prevValue ? '비활성화' : '활성화'}되었습니다.`);
        
        // 기능 활성화 시 해당 탭으로 이동
        if (enabled) {
          updateActiveTab(feature);
        } else {
          // 비활성화된 기능의 탭에 있다면 다른 활성화된 탭으로 이동
          if (activeTab.value === feature) {
            // 다른 활성화된 기능 탭 찾기
            const otherEnabledFeatures = ['share', 'app', 'survey', 'reservation'].filter(
              f => f !== feature && form[`${f}Enabled`]
            );
            if (otherEnabledFeatures.length > 0) {
              updateActiveTab(otherEnabledFeatures[0]);
            }
          }
        }
      } catch (error) {
        console.error(`${feature} 기능 활성화 상태 업데이트 중 오류 발생:`, error);
      }
    };

    // Step navigation - 쿼리 파라미터에서 초기값 설정
    const currentStep = ref(0);

    // 이벤트 필터 관련 상태
    const eventList = ref([]);
    const eventsLoading = ref(false);
    const selectedEventId = ref(null);

    // 자료실 Drawer 상태
    const showResourceDrawer = ref(false);

    // 진행중 변경 확인 모달 관련 상태
    const showCertificateLinkModal = ref(false);
    const originalEventStatus = ref(null);

    // 진행중 이벤트 충돌 모달 관련 상태
    const showOngoingConflictModal = ref(false);
    const conflictingEvent = ref(null);

    /**
     * 주최자별 진행중 이벤트 확인
     * - 단일 활성화 정책: 동일 주최자가 이미 진행중인 이벤트가 있으면 에러
     * @returns {Promise<boolean>} true: 충돌 없음(저장 가능), false: 충돌 있음(저장 불가)
     */
    const checkOngoingEventConflict = async () => {
      // 상태가 'ongoing'이 아니면 충돌 검사 불필요
      if (form.eventStatus !== 'ongoing') {
        return true;
      }

      // organizers가 없으면 충돌 검사 불필요
      if (!form.organizers || form.organizers.length === 0) {
        return true;
      }

      // organizerId 추출
      const organizerIds = form.organizers
        .map(org => org.organizerId || org.id)
        .filter(id => id);

      if (organizerIds.length === 0) {
        return true;
      }

      try {
        return new Promise((resolve) => {
          apiInstance.value.checkOngoingEvent(
            {
              organizerIds,
              excludeEventId: isEdit.value ? form.id : undefined
            },
            (response) => {
              const { hasOngoingEvent, ongoingEvent } = response.data;
              if (hasOngoingEvent && ongoingEvent) {
                // 충돌 발생 - 진행중인 이벤트가 있음
                conflictingEvent.value = ongoingEvent;
                showOngoingConflictModal.value = true;
                resolve(false);
              } else {
                // 충돌 없음
                resolve(true);
              }
            },
            (error) => {
              console.error('진행중 이벤트 확인 실패:', error);
              // API 오류 시에도 저장 진행 허용 (fail-open)
              resolve(true);
            }
          );
        });
      } catch (error) {
        console.error('진행중 이벤트 확인 중 예외 발생:', error);
        return true; // API 오류 시에도 저장 진행 허용
      }
    };

    // 쿼리 파라미터에서 step 값 읽기
    onMounted(() => {
      const stepFromQuery = route.query.step;
      if (stepFromQuery) {
        const stepNumber = parseInt(stepFromQuery, 10);
        // 유효한 범위 내의 값인지 확인 (0-5)
        if (!isNaN(stepNumber) && stepNumber >= 0 && stepNumber <= 5) {
          currentStep.value = stepNumber;
        }
      }
    });

    // currentStep 변경 감지하여 URL 업데이트
    watch(currentStep, (newStep) => {
      // 현재 쿼리 파라미터를 유지하면서 step만 업데이트
      router.replace({
        ...route,
        query: {
          ...route.query,
          step: newStep
        }
      });
    });

    // route params.id 변경 감지 (이벤트 필터에서 다른 이벤트 선택 시)
    watch(
      () => route.params.id,
      async (newEventId, oldEventId) => {
        if (newEventId && newEventId !== oldEventId && apiInstance.value) {
          console.log('이벤트 ID 변경 감지:', oldEventId, '->', newEventId);

          // selectedEventId 업데이트
          selectedEventId.value = newEventId;

          // 데이터 다시 로드
          await loadData(apiInstance.value);

          // 원본 상태 업데이트
          if (isEdit.value) {
            originalEventStatus.value = form.eventStatus;
          }

          // step을 0으로 초기화
          currentStep.value = 0;
        }
      }
    );

    const nextStep = () => {
      // 기본 정보 단계에서 유효성 검사
      if (currentStep.value === 0) {
        // 필수 필드 검증
        if (!form.eventTitle || !form.scenarioType || !form.startDate || !form.endDate) {
          if (instance && instance.proxy && instance.proxy.$message) {
            instance.proxy.$message.warning('기본 정보를 모두 입력해주세요.');
          }
          return;
        }
      }
      currentStep.value++;
    };

    const prevStep = () => {
      currentStep.value--;
    };

    // 뒤로가기 버튼 표시 여부
    const showBackButton = computed(() => {
      return route.query.from === 'participation';
    });

    // 이전 페이지로 돌아가기
    const handleGoBack = () => {
      if (route.query.returnUrl) {
        // returnUrl이 있으면 해당 URL로 이동
        // Vue Router가 자동으로 디코딩하므로 추가 디코딩 불필요
        router.push(route.query.returnUrl);
      } else if (route.query.from === 'participation') {
        // from이 participation인 경우 참여관리 페이지로 이동
        const eventId = route.params.id;
        router.push({
          path: '/events/participation',
          query: { eventId }
        });
      } else {
        // 기본적으로 이벤트 목록으로 이동
        router.push('/events');
      }
    };

    // 이벤트 목록 조회
    const fetchEventList = async () => {
      if (!apiInstance.value) return;

      eventsLoading.value = true;
      try {
        await new Promise((resolve, reject) => {
          apiInstance.value.getEvents(
            { page: 1, limit: 500 },
            (response) => {
              eventList.value = response.data.items || [];
              console.log('이벤트 목록 조회 완료:', eventList.value.length);
              resolve(response);
            },
            (error) => {
              console.error('이벤트 목록 조회 실패:', error);
              reject(error);
            }
          );
        });
      } catch (error) {
        console.error('이벤트 목록 조회 중 오류:', error);
      } finally {
        eventsLoading.value = false;
      }
    };

    // 이벤트 상태 텍스트 변환
    const getEventStatusText = (status) => {
      const statusMap = {
        'pending': '준비중',
        'ongoing': '진행중',
        'completed': '종료됨',
        'paused': '일시중지',
        'cancelled': '취소됨'
      };
      return statusMap[status] || status;
    };

    // 이벤트 필터 검색 옵션
    const filterEventOption = (input, option) => {
      return option.children[0].children.toLowerCase().includes(input.toLowerCase());
    };

    // 이벤트 변경 시 해당 이벤트 편집 페이지로 이동 (확인 모달 포함)
    const handleEventFilterChange = async (newEventId) => {
      if (newEventId && newEventId !== route.params.id) {
        // 현재 이벤트와 새 이벤트 정보 가져오기
        const currentEvent = eventList.value.find(e => e._id === route.params.id);
        const newEvent = eventList.value.find(e => e._id === newEventId);

        // 확인 모달 표시
        const confirmed = await twoBtnDialog(
          '이벤트 이동 확인',
          `현재 편집 중인 이벤트: "${currentEvent?.eventTitle || '알 수 없음'}"\n\n` +
          `"${newEvent?.eventTitle || newEventId}" 이벤트로 이동하시겠습니까?\n\n` +
          `⚠️ 저장하지 않은 변경사항은 사라집니다.`,
          '이동',
          '취소'
        );

        if (confirmed) {
          // 확인 시 새 이벤트 편집 페이지로 이동
          const currentQuery = { ...route.query };
          router.push({
            path: `/events/${newEventId}/edit`,
            query: currentQuery
          });
        } else {
          // 취소 시 selectedEventId를 원래 값으로 복원
          selectedEventId.value = route.params.id;
        }
      }
    };

    // 참여 현황 페이지로 이동
    const goToParticipation = () => {
      const eventId = route.params.id;
      router.push({
        path: '/events/participation',
        query: { eventId }
      });
    };

    // 분석 페이지로 이동
    const goToAnalytics = () => {
      const eventId = route.params.id;
      router.push({
        path: '/events/participation',
        query: { eventId, tab: 'analytics' }
      });
    };

    // 레포트 페이지로 이동
    const goToReport = () => {
      const eventId = route.params.id;
      router.push({
        path: '/events/participation',
        query: { eventId, tab: 'report' }
      });
    };

    // 상단 저장 버튼 핸들러
    const handleSave = async () => {
      try {
        // 🔒 안전 검증: 이벤트 ID 불일치 체크 (다층 방어 전략)
        if (isEdit.value) {
          // 1차 검증: selectedEventId와 form.id 비교
          // (드롭다운에서 다른 이벤트 선택 후 네비게이션 완료 전에 저장 시도하는 경우 방지)
          if (selectedEventId.value && selectedEventId.value !== form.id) {
            if (instance?.proxy?.$message) {
              instance.proxy.$message.error(
                '다른 이벤트가 선택되어 있습니다. 이동을 완료하거나 선택을 취소한 후 저장해주세요.'
              );
            }
            return;
          }

          // 2차 검증: URL params와 form.id 비교
          // (URL과 폼 데이터의 불일치 감지 - 최후의 방어선)
          const urlEventId = route.params.id;
          if (urlEventId && urlEventId !== form.id) {
            if (instance?.proxy?.$message) {
              instance.proxy.$message.error(
                `이벤트 정보 불일치가 감지되었습니다.\n` +
                `URL 이벤트 ID: ${urlEventId}\n` +
                `폼 이벤트 ID: ${form.id}\n` +
                `페이지를 새로고침 후 다시 시도해주세요.`
              );
            }
            console.error('Event ID mismatch detected:', {
              urlEventId,
              formId: form.id,
              selectedEventId: selectedEventId.value
            });
            return;
          }
        }

        // 폼 유효성 검사
        await formRef.value.validate();

        // 진행중 이벤트 충돌 검사 (단일 활성화 정책)
        // - 새 이벤트 생성 시 'ongoing' 상태로 저장하려는 경우
        // - 기존 이벤트를 'ongoing' 상태로 변경하려는 경우
        const willBeOngoing = form.eventStatus === 'ongoing';
        const wasOngoing = isEdit.value && originalEventStatus.value === 'ongoing';

        // 'ongoing' 상태가 되는 경우 충돌 검사 수행
        if (willBeOngoing && !wasOngoing) {
          const canProceed = await checkOngoingEventConflict();
          if (!canProceed) {
            // 충돌 모달이 표시됨 - 저장 중단
            return;
          }
        }

        // 상태가 'ongoing'으로 변경되는지 확인 (수정 시에만 - 진행중 변경 확인 모달용)
        // envConfigKey·ZEISS_CURRENT_EVENT_ID 는 저장 시 서버가 상태에 맞춰 동기화한다
        const isChangingToOngoing = isEdit.value &&
          originalEventStatus.value !== 'ongoing' &&
          form.eventStatus === 'ongoing';

        if (isChangingToOngoing) {
          showCertificateLinkModal.value = true;
          return;
        }

        // 유효성 검사 통과하면 submit
        await handleSubmit(form);
      } catch (error) {
        // 유효성 검사 실패 시
        if (error.errorFields) {
          handleValidationFailed(error);
        }
      }
    };

    // 진행중 변경 확인 모달 취소
    const handleCertificateLinkCancel = () => {
      showCertificateLinkModal.value = false;
    };

    // 진행중 변경 확인 모달 확인
    const handleCertificateLinkConfirm = async () => {
      // 모달 닫기
      showCertificateLinkModal.value = false;

      // 저장 진행 (오류 안내는 handleSubmit 에서 처리)
      await handleSubmit(form);
    };

    // 데이터 로드 및 초기화
    onMounted(async () => {
      // API 인스턴스가 이미 초기화되었으므로 여기서는 loadData만 호출
      if (apiInstance.value) {
        console.log('onMounted: API 인스턴스로 데이터 로드 시작');

        // 데이터 로드 후 원본 상태 저장을 위해 await 처리
        await loadData(apiInstance.value);

        // 수정 모드일 때 원본 상태 저장
        if (isEdit.value) {
          originalEventStatus.value = form.eventStatus;
          console.log('원본 이벤트 상태 저장:', originalEventStatus.value);

          // 이벤트 필터를 위한 이벤트 목록 조회 및 현재 이벤트 선택
          selectedEventId.value = route.params.id;
          await fetchEventList();
        }

        // 초기 활성 탭 설정
        const anyFeatureEnabled = ['share', 'app', 'survey', 'reservation'].some(
          feature => form[`${feature}Enabled`]
        );

        if (!anyFeatureEnabled) {
          form.shareEnabled = true;
          activeTab.value = 'share';
        }
      } else {
        console.error('onMounted: API 인스턴스가 초기화되지 않았습니다.');
        if (instance && instance.proxy && instance.proxy.$message) {
          instance.proxy.$message.error('서버 연결을 초기화할 수 없습니다. 페이지를 새로고침 해주세요.');
        }
      }
    });
    
    
    
    return {
      // useEventForm
      formRef,
      form,
      rules,
      submitting,
      isEdit,
      activeTab,
      showSearchModal,
      handleSubmit,
      handleCancel,
      beforeUpload,
      handleSelectClient,
      loadData,
      handleValidationFailed,

      // Step navigation
      currentStep,
      nextStep,
      prevStep,
      handleSave,

      // 뒤로가기 관련
      showBackButton,
      handleGoBack,

      // 자료실
      showResourceDrawer,

      // 기타
      handleSearchModalOpen,
      showHospitalSearchModal,
      handleHospitalSearchModalOpen,
      handleSelectHospital,
      updateActiveTab,
      updateFeatureEnabled,

      // API instance
      apiInstance,

      // 진행중 변경 확인 모달
      showCertificateLinkModal,
      handleCertificateLinkCancel,
      handleCertificateLinkConfirm,

      // 진행중 이벤트 충돌 모달
      showOngoingConflictModal,
      conflictingEvent,

      // 이벤트 필터 관련
      eventList,
      eventsLoading,
      selectedEventId,
      getEventStatusText,
      filterEventOption,
      handleEventFilterChange,
      goToParticipation,
      goToAnalytics,
      goToReport
    };
  }
};
</script>

<style scoped lang="scss">
.titleContainer {
  display: flex;
  flex-direction: column;
}

.page-title {
  color: #000;
  font-size: 1.8rem;
  margin-bottom: 8px;
}

.pageDesc {
  color: #6c757d;
  font-size: 14px;
}

// 컨트롤 패널 스타일
.control-panel {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

// 이벤트 필터 섹션 스타일
.event-filter-section {
  margin: 16px 0;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  .filter-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #495057;
    margin-bottom: 8px;
  }

  .event-id-display {
    margin-left: 8px;
    font-weight: 400;
  }

  .filter-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  @media (max-width: 768px) {
    .filter-actions {
      justify-content: flex-start;
      margin-top: 12px;
    }
  }
}

// 폼 레이아웃 스타일
.form-card {
  padding: 24px;
  margin-bottom: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  background-color: #fff;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

// 버튼 스타일
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  font-weight: 500 !important;
  height: 40px !important;
  border-radius: 6px !important;
  padding: 0 20px !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
    transform: none;
    box-shadow: none !important;
  }
}

:deep(.ant-btn) {
  height: 40px !important;
  border-radius: 6px !important;
  transition: all 0.2s ease;
}

// 섹션 스타일 통일
:deep(.form-section) {
  margin-bottom: 32px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

:deep(.section-title) {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  padding: 16px 20px;
  margin: 0;
  background-color: #f9f9f9;
  border-bottom: 1px solid #eaeaea;
}

:deep(.section-description) {
  color: #666;
  font-size: 13px;
  margin: 12px 20px;
}

// 마진 조정
:deep(.ant-form-item) {
  margin-bottom: 16px;
}

// 반응형 디자인 개선
@media (max-width: 768px) {
  .form-card {
    padding: 16px;
  }
  
  :deep(.form-section) {
    margin-bottom: 24px;
  }
  
  :deep(.section-title) {
    font-size: 16px;
    padding: 12px 16px;
  }
  
  :deep(.section-description) {
    margin: 8px 16px;
  }
  
  .form-actions {
    flex-wrap: wrap;
  }
  
  :deep(.ant-btn) {
    flex: 1;
    min-width: 120px;
  }
}

// 진행중 이벤트 충돌 모달 스타일
.ongoing-conflict-modal {
  padding: 8px 16px 16px 16px;

  .conflict-warning {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    background-color: #fff7e6;
    border: 1px solid #ffd591;
    border-radius: 8px;
    margin-bottom: 16px;

    .warning-icon {
      font-size: 24px;
      color: #fa8c16;
      margin-top: 2px;
    }

    .warning-message {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      color: #d46b08;
      line-height: 1.5;
    }
  }

  .conflict-details {
    background-color: #f5f5f5;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 16px;

    .detail-item {
      display: flex;
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }

      label {
        font-weight: 600;
        color: #333;
        min-width: 120px;
      }

      span {
        color: #666;
      }
    }
  }

  .conflict-help {
    margin-bottom: 16px;

    p {
      font-size: 13px;
      color: #666;
      line-height: 1.6;
      margin: 0;
    }
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
  }
}

// 진행중 변경 확인 모달 스타일
.certificate-link-modal {
  padding: 8px 16px 16px 16px;

  .modal-description {
    font-size: 14px;
    color: #333;
    margin-bottom: 16px;

    strong {
      color: #4caf50;
    }
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
  }
}
</style>
